'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { motion } from 'framer-motion';
import Icon from '@/components/ui/AppIcon';

interface CounterData {
  label: string;
  value: number;
  suffix: string;
  icon: string;
}

const HeroSection = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [counters, setCounters] = useState<CounterData[]>([
    { label: 'Lives Touched', value: 0, suffix: '+', icon: 'HeartIcon' },
    { label: 'Services Provided', value: 0, suffix: '+', icon: 'CheckCircleIcon' },
    { label: 'Active Volunteers', value: 0, suffix: '+', icon: 'UserGroupIcon' },
    { label: 'Community Events', value: 0, suffix: '+', icon: 'CalendarIcon' },
  ]);

  const targetValues = [15000, 8500, 250, 180];

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (!isHydrated) return;

    const duration = 2000;
    const steps = 60;
    const interval = duration / steps;

    const timer = setInterval(() => {
      setCounters((prev) =>
        prev.map((counter, index) => {
          const target = targetValues[index];
          const increment = target / steps;
          const newValue = Math.min(counter.value + increment, target);
          return { ...counter, value: Math.floor(newValue) };
        })
      );
    }, interval);

    return () => clearInterval(timer);
  }, [isHydrated]);

  if (!isHydrated) {
    return (
      <section className="relative min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/10 via-secondary/5 to-accent/10 pt-16 lg:pt-20">
        <div className="container mx-auto px-4 py-10 sm:py-16 lg:py-20">
          <div className="max-w-4xl mx-auto text-center space-y-6 sm:space-y-8">
            <div className="space-y-4">
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-foreground">
                सेवा ए परमो धर्म
              </h1>
              <p className="text-lg md:text-2xl text-muted-foreground font-accent">
                Service is the Supreme Duty
              </p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="relative min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/10 via-secondary/5 to-accent/10 overflow-hidden pt-16 lg:pt-20">
      {/* Animated Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 left-10 w-72 h-72 bg-primary rounded-full mix-blend-multiply filter blur-xl animate-blob" />
        <div className="absolute top-40 right-10 w-72 h-72 bg-secondary rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-2000" />
        <div className="absolute bottom-20 left-1/2 w-72 h-72 bg-accent rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-4000" />
      </div>

      <div className="container mx-auto px-4 py-10 sm:py-16 lg:py-20 relative z-10">
        <div className="max-w-4xl mx-auto text-center space-y-8 sm:space-y-12">
          {/* Mission Statement */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
            className="space-y-6"
          >
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-4xl md:text-5xl lg:text-6xl font-display font-bold text-foreground leading-tight"
            >
              सेवा ए परमो धर्म
            </motion.h1>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="text-xl md:text-2xl text-muted-foreground font-display italic"
            >
              Service is the Supreme Duty
            </motion.p>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="text-lg md:text-xl text-foreground/80 max-w-3xl mx-auto leading-relaxed"
            >
              Bridging compassionate hearts with those in need through affordable healthcare,
              community support, and dignified service across Surat, Gujarat.
            </motion.p>
          </motion.div>

          {/* Impact Counters */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={{
              hidden: { opacity: 0 },
              visible: {
                opacity: 1,
                transition: {
                  staggerChildren: 0.15,
                  delayChildren: 0.8,
                },
              },
            }}
            className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 py-4 sm:py-8"
          >
            {counters.map((counter, index) => (
              <motion.div
                key={counter.label}
                variants={{
                  hidden: { opacity: 0, y: 50 },
                  visible: { opacity: 1, y: 0 },
                }}
                whileHover={{ scale: 1.05, y: -5 }}
                transition={{ duration: 0.3 }}
                className="glass-card rounded-2xl p-4 sm:p-6 shadow-glass hover:shadow-elevated transition-all duration-300"
              >
                <div className="flex flex-col items-center space-y-3">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.8 + index * 0.15 + 0.2, type: 'spring' }}
                    className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center"
                  >
                    <Icon name={counter.icon as any} size={24} className="text-primary" />
                  </motion.div>
                  <div className="text-center">
                    <div className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-primary">
                      {counter.value.toLocaleString('en-IN')}
                      {counter.suffix}
                    </div>
                    <div className="text-sm text-muted-foreground font-medium mt-1">
                      {counter.label}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Quick Action Buttons */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={{
              hidden: { opacity: 0 },
              visible: {
                opacity: 1,
                transition: {
                  staggerChildren: 0.1,
                  delayChildren: 1.4,
                },
              },
            }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4 md:pt-8"
          >
            <motion.div
              variants={{
                hidden: { opacity: 0, y: 20 },
                visible: { opacity: 1, y: 0 },
              }}
              whileHover={{ scale: 1.05, y: -3 }}
              whileTap={{ scale: 0.95 }}
            >
              <Link
                href="/get-involved"
                className="w-full sm:w-auto px-8 py-4 rounded-xl font-heading font-semibold text-lg bg-accent text-accent-foreground hover:bg-accent/90 transition-all duration-300 shadow-cta hover:shadow-elevated flex items-center justify-center space-x-2"
              >
                <Icon name="HeartIcon" size={24} variant="solid" />
                <span>Donate Now</span>
              </Link>
            </motion.div>
            <motion.div
              variants={{
                hidden: { opacity: 0, y: 20 },
                visible: { opacity: 1, y: 0 },
              }}
              whileHover={{ scale: 1.05, y: -3 }}
              whileTap={{ scale: 0.95 }}
            >
              <Link
                href="/services"
                className="w-full sm:w-auto px-8 py-4 rounded-xl font-heading font-semibold text-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-all duration-300 shadow-cta hover:shadow-elevated flex items-center justify-center space-x-2"
              >
                <Icon name="PlusCircleIcon" size={24} variant="solid" />
                <span>Get Help</span>
              </Link>
            </motion.div>
            <motion.div
              variants={{
                hidden: { opacity: 0, y: 20 },
                visible: { opacity: 1, y: 0 },
              }}
              whileHover={{ scale: 1.05, y: -3 }}
              whileTap={{ scale: 0.95 }}
            >
              <Link
                href="/get-involved"
                className="w-full sm:w-auto px-8 py-4 rounded-xl font-heading font-semibold text-lg bg-secondary text-secondary-foreground hover:bg-secondary/90 transition-all duration-300 shadow-cta hover:shadow-elevated flex items-center justify-center space-x-2"
              >
                <Icon name="UserPlusIcon" size={24} variant="solid" />
                <span>Volunteer</span>
              </Link>
            </motion.div>
          </motion.div>

          {/* Trust Indicators */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.8 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-6 pt-8 text-muted-foreground"
          >
            <motion.div
              whileHover={{ scale: 1.1 }}
              className="flex items-center space-x-2"
            >
              <Icon name="StarIcon" size={20} variant="solid" className="text-accent" />
              <span className="font-semibold text-foreground">4.9/5</span>
              <span className="text-sm">Rating</span>
            </motion.div>
            <div className="hidden sm:block w-px h-6 bg-border" />
            <motion.div
              whileHover={{ scale: 1.1 }}
              className="flex items-center space-x-2"
            >
              <Icon name="UserGroupIcon" size={20} className="text-primary" />
              <span className="font-semibold text-foreground">535+</span>
              <span className="text-sm">Reviews</span>
            </motion.div>
            <div className="hidden sm:block w-px h-6 bg-border" />
            <motion.div
              whileHover={{ scale: 1.1 }}
              className="flex items-center space-x-2"
            >
              <Icon name="CheckBadgeIcon" size={20} variant="solid" className="text-secondary" />
              <span className="text-sm">JustDial Verified</span>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{
          duration: 1,
          delay: 2,
          repeat: Infinity,
          repeatType: 'reverse',
        }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
      >
        <Icon name="ChevronDownIcon" size={32} className="text-primary" />
      </motion.div>
    </section>
  );
};

export default HeroSection;